// src/components/ui/slider.tsx

import * as React from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { cn } from "@/lib/utils";

interface SliderProps extends React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> {
  formatValue?: (value: number) => string;
  showTooltip?: boolean;
  marks?: { value: number; label: string }[];
}

const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  SliderProps
>(({
  className,
  formatValue,
  showTooltip = true,
  marks,
  ...props
}, ref) => {
  const [hoveredValue, setHoveredValue] = React.useState<number | null>(null);
  const [isDragging, setIsDragging] = React.useState(false);

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!showTooltip) return;

    const bounds = event.currentTarget.getBoundingClientRect();
    const percentage = (event.clientX - bounds.left) / bounds.width;
    const value = Math.round(
      percentage * ((props.max || 100) - (props.min || 0)) + (props.min || 0)
    );
    setHoveredValue(value);
  };

  return (
    <div className="space-y-4">
      <SliderPrimitive.Root
        ref={ref}
        className={cn(
          "relative flex w-full touch-none select-none items-center",
          className
        )}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoveredValue(null)}
        onPointerDown={() => setIsDragging(true)}
        onPointerUp={() => setIsDragging(false)}
        {...props}
      >
        <SliderPrimitive.Track className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-secondary">
          <SliderPrimitive.Range className="absolute h-full bg-primary" />
        </SliderPrimitive.Track>
        {props.value?.map((value, index) => (
          <SliderPrimitive.Thumb
            key={index}
            className="block h-4 w-4 rounded-full border border-primary/50 bg-background shadow transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
          >
            {(showTooltip && (isDragging || hoveredValue !== null)) && (
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 -translate-y-1 bg-primary text-primary-foreground text-xs px-2 py-1 rounded shadow">
                {formatValue ? formatValue(value) : value}
              </div>
            )}
          </SliderPrimitive.Thumb>
        ))}
      </SliderPrimitive.Root>

      {marks && (
        <div className="flex justify-between px-1">
          {marks.map((mark) => (
            <div
              key={mark.value}
              className="flex flex-col items-center"
              style={{
                left: `${((mark.value - (props.min || 0)) / ((props.max || 100) - (props.min || 0))) * 100}%`,
              }}
            >
              <div className="w-0.5 h-1 bg-gray-300" />
              <span className="text-xs text-gray-500 mt-1">{mark.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

Slider.displayName = SliderPrimitive.Root.displayName;

export { Slider };